import { Box, CircularProgress } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CUSTOM_SCHEME_PATH } from 'services/api-constants';
import { logError } from 'services/logging';
import { downloadFile } from 'utils/download-file';
import getFirebaseURLFilename from 'utils/get-firebase-url-filename';
import getGcsURLFilename from 'utils/get-gcs-url-filename';
import isGcsUrl from 'utils/is-gcs-url';
import { isIOS } from 'utils/is-ios';
import useHandleGQLOperation from './useHandleGQLOperation';
import { SnackbarVariant, useSnackbar } from './useSnackBar';

const useHandleFileDownload = (onIsLoading: (isLoading: boolean) => void) => {
  const { handleGQLOperation } = useHandleGQLOperation();
  const { showSnackbar } = useSnackbar();
  const isIOSDevice = isIOS();

  // Track pending downloads
  const pendingDownloadsRef = useRef<number>(0);
  // Track if downloads are in progress for iOS overlay
  const [isDownloading, setIsDownloading] = useState(false);

  // Helper function to decrement pending downloads and hide loader when all complete
  const decrementPendingDownloads = useCallback(() => {
    pendingDownloadsRef.current--;
    if (pendingDownloadsRef.current === 0) {
      onIsLoading(false);
      if (isIOSDevice) {
        setIsDownloading(false);
      }
    }
  }, [onIsLoading, isIOSDevice]);

  // Listen for download status messages from Swift (iOS only)
  useEffect(() => {
    if (!isIOSDevice) return;

    const messageHandler = (event: MessageEvent) => {
      // WKWebView safety check
      if (event.source !== window) return;
      if (!event.data || typeof event.data !== 'object') return;

      const { action } = event.data as {
        action?: 'downloadComplete' | 'downloadError';
      };

      if (!action) return;

      if (action === 'downloadComplete') {
        decrementPendingDownloads();
      }

      if (action === 'downloadError') {
        decrementPendingDownloads();
        showSnackbar({ message: 'Failed to download a file' }, SnackbarVariant.ERROR);
      }
    };

    window.addEventListener('message', messageHandler);
    return () => window.removeEventListener('message', messageHandler);
  }, [isIOSDevice, decrementPendingDownloads, showSnackbar]);

  const handleFileDownload = useCallback(
    async (urls: string) => {
      try {
        onIsLoading(true);
        const urlArray = urls.split(';').filter((url) => url.trim().length > 0);
        pendingDownloadsRef.current = urlArray.length;
        if (isIOSDevice) {
          setIsDownloading(true);
        }

        for (const url of urlArray) {
          let downloadUrl = url;

          // Check if this is a GQL URL and resolve it first
          if (downloadUrl.startsWith(`${CUSTOM_SCHEME_PATH.GQL}:`)) {
            try {
              const { url: gqlResponse, errorMessage } = await handleGQLOperation(downloadUrl);
              if (gqlResponse) {
                downloadUrl = gqlResponse;
              } else {
                // If it was a GQL URL but resolution failed, log error and skip this URL
                // Avoids bad download url
                logError(new Error('Failed to resolve GQL URL'), {
                  extra: {
                    gqlUrl: downloadUrl,
                    underlyingError: errorMessage,
                    urlOnError: window.location.href,
                  },
                });
                decrementPendingDownloads();
                continue;
              }
            } catch (error) {
              // If GQL operation throws an error, log and skip this URL
              logError(new Error('Failed to resolve GQL URL'), {
                extra: {
                  gqlUrl: downloadUrl,
                  underlyingError: error instanceof Error ? error.message : String(error),
                  urlOnError: window.location.href,
                },
              });
              decrementPendingDownloads();
              continue;
            }
          }

          // Extract filename and extension
          const filename = isGcsUrl(downloadUrl) ? getGcsURLFilename(downloadUrl) : getFirebaseURLFilename(downloadUrl);

          try {
            // On iOS: sends message to Swift, status updates come via message listener in useEffect
            // On non-iOS: standard blob download
            await downloadFile(downloadUrl, filename);

            // For non-iOS, decrement immediately after download
            // For iOS, message listener handles pendingDownloadsRef.current
            if (!isIOSDevice) {
              decrementPendingDownloads();
            }
          } catch (error) {
            logError(error);
            decrementPendingDownloads();
          }
        }
      } catch (e) {
        logError(e);
        onIsLoading(false);
        if (isIOSDevice) {
          setIsDownloading(false);
        }
      }
    },
    [onIsLoading, handleGQLOperation, decrementPendingDownloads, isIOSDevice],
  );

  // Loading overlay component for iOS
  const LoadingOverlay: React.FC = () => {
    if (!isIOSDevice || !isDownloading) return null;
    return React.createElement(
      Box,
      {
        sx: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        },
      },
      React.createElement(CircularProgress, { size: 40, sx: { color: 'white' } }),
    );
  };

  return { handleFileDownload, LoadingOverlay };
};

export default useHandleFileDownload;
